import React, {
  FormEvent,
  FunctionComponent,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '@illegally-parked/routes/Routes';
import { DateAndTimeTemplate } from '@templates';
import { ErrorMessages } from '@types';
import { useParkingStore } from '@illegally-parked/store/store';
import {
  parseZodErrors,
  radioOptionsSchema,
  dateAndTimeSchema,
  dateAndTimeRangeSchema,
  TextAreaMaxLengthSchema,
  dynamicDateErrorMessage,
  dynamicTimeErrorMessage,
  dynamicDateRangeErrorMessage,
  dynamicTimeRangeErrorMessage,
  getDateDetails,
} from '@utils';
import {
  NavigationButtons,
  RadioOptions,
  TextArea,
  TextInputMolecule,
} from '@molecules/*';
import { TEXT_AREA_MAX_LENGTH } from '@constants';
import { ErrorSummary } from '@atoms/*';

const pageTitle = 'Date and time';
const description = 'Is the issue happening now?';

const otherDateTimeSeenText = 'Other dates and times seen (optional)';
const otherSeenErrorSummary =
  'Other dates and times seen field must be 500 characters or less';
const otherInfoText = 'Other information (optional)';
const otherInfoErrorSummary =
  'Other information field must be 500 characters or less';

export const DateTimeDetails: FunctionComponent = () => {
  const navigate = useNavigate();
  const {
    isIssueHappeningNow,
    updateIsIssueHappeningNow,
    otherDatesTimesSeen,
    updateOtherDatesTimesSeen,
    otherDatesTimesMoreInfo,
    updateOtherDatesTimesMoreInfo,
    selectedDateTime,
    updateSelectedDateTime,
  } = useParkingStore();

  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const [dateErrormessage, setDateErrorMessage] = useState('');
  const [timeErrorMessage, setTimeErrorMessage] = useState('');

  const [dateRangeErrormessage, setDateRangeErrorMessage] = useState('');
  const [timeRangeErrorMessage, setTimeRangeErrorMessage] = useState('');

  const resolvedErrorMessages =
    dateErrormessage || timeErrorMessage
      ? [
          { path: 'dateErrorPath', message: dateErrormessage },
          { path: 'timeErrorMessage', message: timeErrorMessage },
        ]
      : errorMessages;

  const resolvedDateRangeErrorMessages =
    dateRangeErrormessage || timeRangeErrorMessage
      ? [
          { path: 'dateRangeErrorPath', message: dateRangeErrormessage },
          { path: 'timeRangeErrorPath', message: timeRangeErrorMessage },
        ]
      : errorMessages;

  const handleChange = (value: string) => {
    updateIsIssueHappeningNow(value);
    setErrorMessages([]);
  };

  const handleChangeDateTimeSeen = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    updateOtherDatesTimesSeen(event.target.value);
    setErrorMessages([]);
  };

  const handleChangeInfo = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateOtherDatesTimesMoreInfo(event.target.value);
    setErrorMessages([]);
  };

  useEffect(() => {
    if (isIssueHappeningNow.length === 0) return;
    if (isIssueHappeningNow.toLowerCase() === 'yes') {
      const dateDetails = getDateDetails();
      updateSelectedDateTime({
        selectedDay: dateDetails.day.toString(),
        selectedMonth: dateDetails.month.toString(),
        selectedYear: dateDetails.year.toString(),
        selectedHour: dateDetails.hour.toString(),
        selectedMinute: dateDetails.minute.toString(),
      });
    }
  }, [isIssueHappeningNow]);

  const handleClick = () => {
    try {
      radioOptionsSchema('isIssueHappeningNow').parse({ isIssueHappeningNow });

      isIssueHappeningNow.toLowerCase() === 'no' &&
        dateAndTimeSchema.parse(selectedDateTime);
      isIssueHappeningNow.toLowerCase() === 'no' &&
        dateAndTimeRangeSchema.parse({
          yearRange: selectedDateTime.selectedYear,
          monthRange: selectedDateTime.selectedMonth,
          dayRange: selectedDateTime.selectedDay,
          hourRange: selectedDateTime.selectedHour,
          minuteRange: selectedDateTime.selectedMinute,
        });

      TextAreaMaxLengthSchema({
        fieldNames: ['otherDatesTimesSeen'],
        maxLength: TEXT_AREA_MAX_LENGTH,
        maxLengthMessage: otherSeenErrorSummary,
      }).parse({
        otherDatesTimesSeen,
      });

      TextAreaMaxLengthSchema({
        fieldNames: ['otherDatesTimesMoreInfo'],
        maxLength: TEXT_AREA_MAX_LENGTH,
        maxLengthMessage: otherInfoErrorSummary,
      }).parse({
        otherDatesTimesMoreInfo,
      });

      navigate(RoutesList.EMAIL_CONFIRMATION_REPORT);
    } catch (error: any) {
      const errors = parseZodErrors(error);
      const errorPaths = errors.map((error) => error.path);

      const newDateErrorMessage = dynamicDateErrorMessage(errorPaths);
      setDateErrorMessage(newDateErrorMessage!);

      const newTimeErrorMessage: string | undefined =
        dynamicTimeErrorMessage(errorPaths);
      setTimeErrorMessage(newTimeErrorMessage!);

      const newDateRangeErrorMessage = dynamicDateRangeErrorMessage(
        errorPaths,
        errors,
      );
      setDateRangeErrorMessage(newDateRangeErrorMessage!);

      const newTimeRangeErrorMessage: string | undefined =
        dynamicTimeRangeErrorMessage(errorPaths, errors);
      setTimeRangeErrorMessage(newTimeRangeErrorMessage!);

      setErrorMessages(errors);
    }
  };

  const resetErrorMessages = () => {
    setDateErrorMessage('');
    setTimeErrorMessage('');
    setErrorMessages([]);
    setDateRangeErrorMessage('');
    setTimeRangeErrorMessage('');
  };

  const handleDateInput = (e: FormEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;

    const updatedDateTime = {
      ...selectedDateTime,
      [name]: value,
    };

    updateSelectedDateTime(updatedDateTime);
    resetErrorMessages();
  };

  const buttonPrevious = {
    onClick: () => navigate(RoutesList.ISSUE_LOCATION),
  };

  const buttonNext = {
    label: 'Next',
    onClick: handleClick,
  };

  const options = [
    {
      labelText: 'Yes',
      isDefault: isIssueHappeningNow === 'Yes',
    },
    {
      labelText: 'No',
      isDefault: isIssueHappeningNow === 'No',
    },
  ];

  const getErrorMessages = (path: string) => {
    return errorMessages?.find((error) => error.path === path)
      ? errorMessages
      : undefined;
  };

  return (
    <>
      <ErrorSummary
        messages={
          resolvedErrorMessages.length > 0
            ? resolvedErrorMessages
            : resolvedDateRangeErrorMessages.length > 0
              ? resolvedDateRangeErrorMessages
              : errorMessages
        }
      />

      <RadioOptions
        title={pageTitle}
        isLabelTextTitle={true}
        description={description}
        onChange={(value) => handleChange(value)}
        options={options}
        errorPath="isIssueHappeningNow"
        errorMessages={getErrorMessages('isIssueHappeningNow')}
        radioClassName="-mt-2"
      />

      {isIssueHappeningNow === 'No' ? (
        <div className="mt-5">
          <DateAndTimeTemplate
            labelText="Date seen"
            description="For example, 31 01 2024"
            errorPath={
              dateErrormessage?.length > 0
                ? 'dateErrorPath'
                : 'dateRangeErrorPath'
            }
            errorMessages={
              dateErrormessage?.length > 0
                ? resolvedErrorMessages
                : resolvedDateRangeErrorMessages
            }
          >
            <div className="flex gap-x-4">
              <section className="w-12">
                <TextInputMolecule
                  maxLength={2}
                  label="Day"
                  name="selectedDay"
                  errorPath="dayRange"
                  value={selectedDateTime.selectedDay}
                  onChange={(e) => handleDateInput(e)}
                />
              </section>
              <section className="w-12">
                <TextInputMolecule
                  maxLength={2}
                  label="Month"
                  name="selectedMonth"
                  value={selectedDateTime.selectedMonth}
                  errorPath="monthRange"
                  onChange={(e) => handleDateInput(e)}
                />
              </section>
              <section className="w-16">
                <TextInputMolecule
                  maxLength={4}
                  label="Year"
                  name="selectedYear"
                  errorPath="yearRange"
                  value={selectedDateTime.selectedYear}
                  onChange={(e) => handleDateInput(e)}
                />
              </section>
            </div>
          </DateAndTimeTemplate>

          <div className="mt-5">
            <DateAndTimeTemplate
              labelText="Time seen"
              description="Enter time in 24h format"
              errorPath={
                timeErrorMessage?.length > 0
                  ? 'timeErrorMessage'
                  : 'timeRangeErrorPath'
              }
              errorMessages={
                timeErrorMessage?.length > 0
                  ? resolvedErrorMessages
                  : resolvedDateRangeErrorMessages
              }
            >
              <div className="flex gap-x-6">
                <section className="w-12">
                  <TextInputMolecule
                    maxLength={2}
                    label="Hours"
                    name="selectedHour"
                    errorPath="hourRange"
                    value={selectedDateTime.selectedHour}
                    onChange={(e) => handleDateInput(e)}
                  />
                </section>
                <section className="w-12">
                  <TextInputMolecule
                    maxLength={2}
                    label="Minute"
                    name="selectedMinute"
                    errorPath="minuteRange"
                    value={selectedDateTime.selectedMinute}
                    onChange={(e) => handleDateInput(e)}
                  />
                </section>
              </div>
            </DateAndTimeTemplate>
          </div>
        </div>
      ) : null}

      <TextArea
        labelText={otherDateTimeSeenText}
        isLabelTextTitle={false}
        value={otherDatesTimesSeen}
        onChange={handleChangeDateTimeSeen}
        errorPath="otherDatesTimesSeen"
        errorMessages={getErrorMessages('otherDatesTimesSeen')}
        showCounter={true}
        maxLength={TEXT_AREA_MAX_LENGTH}
      />

      <TextArea
        labelText={otherInfoText}
        isLabelTextTitle={false}
        value={otherDatesTimesMoreInfo}
        onChange={handleChangeInfo}
        errorPath="otherDatesTimesMoreInfo"
        errorMessages={getErrorMessages('otherDatesTimesMoreInfo')}
        showCounter={true}
        maxLength={TEXT_AREA_MAX_LENGTH}
      />

      <NavigationButtons
        buttonNext={buttonNext}
        buttonPrevious={buttonPrevious}
      />
    </>
  );
};
